import React from 'react';
import { T, Var, useGT } from 'gt-react';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  WarningCircleIcon,
  WarningIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import MDTypography from 'src/components/md/MDTypography';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';

interface SelectedVariantSummaryProps {
  variantId: string;
}

/**
 * Component that displays the currently selected product variant
 * with a prominent visual indicator showing it's the active selection
 */
const SelectedVariantSummary: React.FC<SelectedVariantSummaryProps> = ({
  variantId,
}) => {
  const gt = useGT();
  const { data, isLoading, error } = useProductVariant(variantId, {
    enabled: !!variantId,
    includeInactive: true,
  });
  const productVariant = data?.productVariant;
  const attributeDescriptions = data?.attributeDescriptions || [];
  const hasAttributes = attributeDescriptions.length > 0;
  const isInactive = productVariant?.active === false;

  if (isLoading) {
    return (
      <div className="mt-4 p-4 border border-[rgba(0,164,164,0.3)] rounded bg-[rgba(0,164,164,0.05)] flex items-center">
        <Spinner size="sm" className="mr-2" />
        <MDTypography variant="body2" color="#00a4a4">
          <T context="loading state for selected product variant summary">
            Loading selected variant information...
          </T>
        </MDTypography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 p-4 border border-[rgba(211,47,47,0.3)] rounded bg-[rgba(211,47,47,0.05)] flex items-center">
        <WarningCircleIcon size={20} className="mr-2" />
        <MDTypography variant="body2" color="error.main">
          {error.message ||
            gt('Error loading selected variant', {
              context: 'error loading product variant in summary',
            })}
        </MDTypography>
      </div>
    );
  }

  if (!productVariant) {
    return null;
  }

  return (
    <div
      className={`mt-4 p-4 border rounded ${isInactive ? 'border-[rgba(239,68,68,0.4)] bg-[rgba(239,68,68,0.05)]' : 'border-[rgba(0,164,164,0.3)] bg-[rgba(0,164,164,0.05)]'}`}
    >
      {/* Header section with clear indication this is currently selected */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center">
          {isInactive ? (
            <WarningIcon
              size={20}
              className="mr-1.5"
              style={{ color: '#ef4444' }}
            />
          ) : (
            <CheckCircleIcon
              size={20}
              className="mr-1.5"
              style={{ color: '#00a4a4' }}
            />
          )}
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={isInactive ? '#ef4444' : '#00a4a4'}
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {isInactive ? (
              <T context="warning that selected variant is inactive">
                Inactive Variant - Must Be Changed
              </T>
            ) : (
              <T context="label for currently selected product variant">
                Currently Selected Product Variant
              </T>
            )}
          </MDTypography>
        </div>
      </div>

      {/* Product variant details */}
      <div>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div>
              <MDTypography
                variant="subtitle2"
                fontWeight="medium"
                color={isInactive ? '#ef4444' : '#00a4a4'}
              >
                {(productVariant.originMetadata?.NAMEINVC as string) ||
                  productVariant.sku ||
                  gt('Selected Variant', {
                    context: 'fallback title when variant has no name',
                  })}
              </MDTypography>
              {productVariant.sku && (
                <MDTypography variant="caption" color="text.secondary">
                  <T context="stock keeping unit label with value">
                    SKU: <Var>{productVariant.sku}</Var>
                  </T>
                </MDTypography>
              )}
            </div>
          </div>
        </div>

        {/* Display attributes if available */}
        {hasAttributes && (
          <div className="flex flex-wrap gap-[6px] mt-3 max-w-full">
            {attributeDescriptions.map((attr, index) => (
              <Badge
                key={index}
                variant="outline"
                className={
                  isInactive
                    ? 'border-red-400/40 text-red-600 bg-red-500/[0.08] text-[0.7rem]'
                    : 'border-teal-400/40 text-teal-700 bg-teal-500/[0.08] text-[0.7rem]'
                }
              >
                <span className="font-medium opacity-90">
                  <T context="product attribute name before value">
                    <Var>{attr.name}</Var>:
                  </T>
                </span>{' '}
                <span className="opacity-80">{attr.value}</span>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectedVariantSummary;
